import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommentSection from '@/components/feature/CommentSection';

import { useAuth } from '@/hooks/useAuth';
import { fetchPublicDemandDetail, fetchPublicDemands, submitDemandBid } from '@/service/api/demand';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';

/** 格式化日期为 YYYY-MM-DD */
function formatDateShort(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** 格式化完整日期 */
function formatDateTime(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** 格式化预算展示文本 */
function formatBudgetDisplay(budgetDisplay?: string, budgetType?: string): string {
  if (!budgetDisplay || budgetDisplay.trim() === '') {
    return budgetType === 'NEGOTIABLE' ? '面议' : '面议';
  }
  if (budgetDisplay.includes('面议') || budgetType === 'NEGOTIABLE') {
    return '面议';
  }

  const rangeMatch = budgetDisplay.match(/^(\d+)\s*-\s*(\d+)\s*(CNY|[A-Z]{3})?$/i);
  if (rangeMatch) {
    const num1 = parseInt(rangeMatch[1], 10);
    const num2 = parseInt(rangeMatch[2], 10);
    const str1 = num1 >= 10000 ? `${(num1 / 10000).toFixed(num1 % 10000 === 0 ? 0 : 1)}万` : `${num1}`;
    const str2 = num2 >= 10000 ? `${(num2 / 10000).toFixed(num2 % 10000 === 0 ? 0 : 1)}万` : `${num2}`;
    return `${str1} - ${str2}元`;
  }

  const singleMatch = budgetDisplay.match(/^(\d+)\s*(CNY|[A-Z]{3})?$/i);
  if (singleMatch) {
    const num = parseInt(singleMatch[1], 10);
    const str = num >= 10000 ? `${(num / 10000).toFixed(num % 10000 === 0 ? 0 : 1)}万` : `${num}`;
    return `${str}元`;
  }

  return budgetDisplay.replace(/\bCNY\b/gi, '元').trim();
}

/** 格式化文件大小 */
function formatFileSize(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** 状态样式配置 */
const statusConfigMap: Record<string, { bg: string; color: string; label: string }> = {
  RECRUITING: { bg: 'bg-primary-50 border-primary-200', color: 'text-primary-600', label: '竞标中' },
  open: { bg: 'bg-primary-50 border-primary-200', color: 'text-primary-600', label: '竞标中' },
  CLOSED: { bg: 'bg-gray-100 border-gray-200', color: 'text-foreground-500', label: '已结束' },
  closed: { bg: 'bg-gray-100 border-gray-200', color: 'text-foreground-500', label: '已结束' },
  COMPLETED: { bg: 'bg-green-50 border-green-200', color: 'text-green-600', label: '已完成' },
  awarded: { bg: 'bg-green-50 border-green-200', color: 'text-green-600', label: '已中标' },
  DRAFT: { bg: 'bg-amber-50 border-amber-200', color: 'text-amber-600', label: '草稿' },
  PENDING_REVIEW: { bg: 'bg-blue-50 border-blue-200', color: 'text-blue-600', label: '待审核' },
};

export default function DemandDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isLoggedIn, user } = useAuth();

  const [demand, setDemand] = useState<Api.Demand.DemandResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarDemands, setSimilarDemands] = useState<Api.Demand.DemandResponse[]>([]);

  const [showBidModal, setShowBidModal] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [bidPrice, setBidPrice] = useState('');
  const [bidDays, setBidDays] = useState('');
  const [bidNote, setBidNote] = useState('');
  const [bidSubmitted, setBidSubmitted] = useState(false);
  const [bidError, setBidError] = useState('');
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();

  const categoriesFetchedRef = useRef(false);
  const fetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (categoriesFetchedRef.current || categories.length > 0) return;
    categoriesFetchedRef.current = true;
    fetchCategories().catch((err) => {
      console.error('获取分类失败:', err);
      categoriesFetchedRef.current = false;
    });
  }, [categories.length, fetchCategories]);

  useEffect(() => {
    if (!id) return;
    if (fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;

    setLoading(true);
    setError(null);

    fetchPublicDemandDetail(id)
      .then((res) => {
        setDemand(res || null);
        // 获取同类推荐需求
        const categoryCode = res?.category_codes?.[0];
        fetchPublicDemands({ category_code: categoryCode, page: 1, page_size: 4 })
          .then((listRes) => {
            const list = (listRes?.items || []).filter((item) => item.id !== id);
            setSimilarDemands(list.slice(0, 3));
          })
          .catch(() => { });
      })
      .catch((err) => {
        console.error('获取需求详情失败:', err);
        setError('获取需求详情失败，该需求可能已不存在');
        fetchedIdRef.current = null;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <DetailPageLayout>
        <div className="pt-20 sm:pt-24 pb-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm text-foreground-500">正在加载需求详情...</p>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !demand) {
    return (
      <DetailPageLayout>
        <div className="pt-20 sm:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-950 mb-2">需求未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">{error || '该需求可能已关闭或不存在'}</p>
            <Link to="/demands" className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer">
              返回需求大厅
            </Link>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const categoryNames =
    demand.category_codes && demand.category_codes.length > 0
      ? demand.category_codes.map((code) => getCategoryNameByCode(code, code))
      : ['需求'];

  const statusKey = demand.status || 'RECRUITING';
  const statusStyle = statusConfigMap[statusKey] || statusConfigMap['RECRUITING'];
  const hasUserBidded = demand.bid_status === 'BID' || bidSubmitted;
  const budgetText = formatBudgetDisplay(demand.budget_display, demand.budget_type);

  const isSelfPublished = Boolean(
    isLoggedIn &&
    user &&
    (demand.publisher_account_id
      ? user.id === demand.publisher_account_id
      : (demand as any).author_account_id
        ? user.id === (demand as any).author_account_id
        : false)
  );

  const requirements =
    demand.undertaking_requirements && demand.undertaking_requirements.length > 0
      ? demand.undertaking_requirements
      : demand.undertaking_requirements_summary && demand.undertaking_requirements_summary.length > 0
        ? demand.undertaking_requirements_summary
        : [];

  const deadlineText = formatDateShort(demand.bid_deadline_at);
  const deliveryText = formatDateShort(demand.expected_delivery_at);
  const publishedText = formatDateTime(demand.published_at);

  const handleBidClick = () => {
    if (!isLoggedIn || user?.market_residency_status !== 'ACTIVE') {
      setShowLoginPrompt(true);
      return;
    }
    setShowBidModal(true);
    setBidError('');
  };

  const handleBidSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBidError('');
    if (!bidPrice.trim() || isNaN(Number(bidPrice)) || Number(bidPrice) <= 0) {
      setBidError('请输入有效的报价金额');
      return;
    }
    if (!bidDays.trim() || isNaN(Number(bidDays)) || Number(bidDays) <= 0) {
      setBidError('请输入有效的交付周期');
      return;
    }
    if (bidNote.length > 500) {
      setBidError('竞标说明不能超过 500 字');
      return;
    }
    if (!id) return;

    setIsSubmittingBid(true);
    try {
      await submitDemandBid(id, {
        currency: 'CNY',
        quoted_amount: bidPrice.trim(),
        expected_delivery_days: Number(bidDays),
        message: bidNote.trim()
      });
      setBidSubmitted(true);
      setDemand((prev) => (prev ? { ...prev, bid_status: 'BID' } : prev));
    } catch (err: any) {
      setBidError(err?.message || '提交报价失败，请稍后重试');
    } finally {
      setIsSubmittingBid(false);
    }
  };

  const closeModal = () => {
    setShowBidModal(false);
    setShowLoginPrompt(false);
    setBidSubmitted(false);
    setBidPrice('');
    setBidDays('');
    setBidNote('');
    setBidError('');
  };

  return (
    <DetailPageLayout
      header={
        <>
          {/* Breadcrumb */}
          <div className="pt-16 sm:pt-20">
            <div className="detail-shell">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '需求大厅', href: '/demands' },
                  { label: demand.title },
                ]}
              />
            </div>
          </div>

          <section className="py-6 sm:py-8 bg-gradient-to-b from-gray-50 via-gray-50/60 to-white border-b border-gray-100/80">
            <div className="detail-shell">
              {/* Title Row */}
              <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {categoryNames.map((catName, idx) => (
                    <span key={idx} className="text-xs px-3 py-1 rounded-full font-medium bg-purple-50 text-purple-600 border border-purple-100/60">
                      {catName}
                    </span>
                  ))}
                  {statusStyle && (
                    <span className={`text-xs px-3 py-1 rounded-full border font-medium ${statusStyle.bg} ${statusStyle.color}`}>
                      {statusStyle.label}
                    </span>
                  )}
                </div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-tight">
                  {demand.title}
                </h1>
              </div>
              {/* Hero Budget Badge */}
              <div className="shrink-0 flex flex-col items-center sm:items-end px-6 py-3.5 rounded-2xl bg-white border border-gray-200/80 shadow-2xs">
                <span className="text-xs text-foreground-400 mb-1 font-medium">需求预算</span>
                <span className="text-xl md:text-2xl font-bold text-red-500">{budgetText}</span>
              </div>
            </div>
          </div>
        </section>
      </>
      }
      sidebar={
        <>
          {/* Company Card */}
          <div className="rounded-2xl bg-white border border-gray-200/80 p-6 shadow-2xs">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
                <i className="ri-building-line text-xl text-secondary-600"></i>
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-foreground-950 text-sm truncate">
                  {demand.enterprise_name_masked || '需求发布方'}
                </p>
                <p className="text-xs text-foreground-400">需求发布企业</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-foreground-500">
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-foreground-600 border border-gray-200/60">
                <i className="ri-shield-check-line mr-1 text-secondary-500 text-xs"></i>
                企业认证
              </span>
            </div>
          </div>

          {/* Timeline Card */}
          <div className="rounded-2xl bg-white border border-gray-200/80 p-6 shadow-2xs">
            <h3 className="font-heading font-bold text-sm text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-history-line text-secondary-500"></i>
              需求时间线
            </h3>
            <div className="relative pl-6 space-y-4 before:absolute before:left-[9px] before:top-2 before:bottom-2 before:w-px before:bg-background-200">
              {publishedText && (
                <div className="relative">
                  <div className="absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full bg-primary-500 ring-4 ring-primary-100"></div>
                  <p className="text-xs font-medium text-foreground-950">需求发布</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{publishedText}</p>
                </div>
              )}
              {deadlineText && (
                <div className="relative">
                  <div className={`absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full ring-4 ${statusKey === 'CLOSED' || statusKey === 'closed' ? 'bg-gray-400 ring-gray-100' : 'bg-primary-500 ring-primary-100'}`}></div>
                  <p className="text-xs font-medium text-foreground-950">招募截止时间</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{deadlineText}</p>
                </div>
              )}
              {deliveryText && (
                <div className="relative">
                  <div className="absolute -left-[22px] top-1 w-2.5 h-2.5 rounded-full bg-blue-500 ring-4 ring-blue-100"></div>
                  <p className="text-xs font-medium text-foreground-950">预计交付时间</p>
                  <p className="text-xs text-foreground-400 mt-0.5">{deliveryText}</p>
                </div>
              )}
            </div>
          </div>

          {/* Bid / Status CTA */}
          <div className="rounded-2xl bg-white border border-gray-200/80 p-5 shadow-2xs">
            {statusKey === 'CLOSED' || statusKey === 'closed' || statusKey === 'COMPLETED' ? (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-gray-100 flex items-center justify-center">
                  <i className="ri-lock-line text-xl text-foreground-400"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-950 mb-1">招募已结束</p>
                <p className="text-xs text-foreground-400">该需求不再接受新的竞标参与</p>
              </div>
            ) : isSelfPublished ? (
              <div className="text-center">
                <Link
                  to={`/my-demands/${demand.id || id}`}
                  className="block w-full text-center px-5 py-2.5 rounded-xl bg-primary-50 text-primary-600 border border-primary-200 text-sm font-semibold hover:bg-primary-100 transition-all cursor-pointer whitespace-nowrap"
                >
                  管理我的需求
                </Link>
              </div>
            ) : hasUserBidded ? (
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-50 border border-emerald-200/60 flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-2xl text-emerald-500"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-950 mb-1">您已提交竞标</p>
                <p className="text-xs text-foreground-500 mb-4">竞标方案已成功发送给发布方，请等待后续反馈</p>
                <button
                  disabled
                  className="w-full px-5 py-2.5 rounded-xl bg-gray-100 text-foreground-400 text-sm font-semibold cursor-not-allowed whitespace-nowrap"
                >
                  已参与竞标
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-4">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-100 flex items-center justify-center">
                    <i className="ri-auction-line text-xl text-primary-600"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-950 mb-1">需求正在招募中</p>
                  <p className="text-xs text-foreground-500">
                    {!isLoggedIn
                      ? '登录后即可参与需求竞标与沟通'
                      : user?.market_residency_status !== 'ACTIVE'
                        ? '完成市场入驻后即可参与需求竞标'
                        : '提交您的报价方案与交付周期'}
                  </p>
                </div>
                <button
                  onClick={handleBidClick}
                  className="w-full px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:brightness-95 transition-all cursor-pointer whitespace-nowrap shadow-sm"
                >
                  立即竞标
                </button>
              </>
            )}
          </div>

          {/* Contact Info Block */}
          {!isLoggedIn && (
            <div className="rounded-2xl bg-white border border-gray-200/80 p-5 text-center shadow-2xs">
              <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-gray-100 flex items-center justify-center">
                <i className="ri-lock-line text-foreground-400"></i>
              </div>
              <p className="text-sm font-medium text-foreground-950 mb-1">联系方式</p>
              <p className="text-xs text-foreground-400 mb-4">登录后可查看发布方相关联系方式</p>
              <Link
                to="/auth"
                className="block w-full text-center px-5 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:brightness-95 transition-all cursor-pointer whitespace-nowrap"
              >
                登录 / 注册
              </Link>
            </div>
          )}

          {/* Similar Demands */}
          {similarDemands.length > 0 && (
            <div className="rounded-2xl bg-white border border-gray-200/80 p-5 shadow-2xs">
              <h3 className="font-heading font-bold text-sm text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-secondary-500"></i>
                同类需求推荐
              </h3>
              <div className="space-y-3">
                {similarDemands.map((d) => (
                  <Link
                    key={d.id}
                    to={`/demands/${d.id}`}
                    className="block group"
                  >
                    <div className="p-3 rounded-xl bg-white border border-gray-200/60 hover:border-primary-200/60 transition-all">
                      <p className="text-xs font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {d.title}
                      </p>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-red-500">
                          {formatBudgetDisplay(d.budget_display, d.budget_type)}
                        </span>
                        <span className="text-foreground-400">查看详情</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      }
      extra={
        <div className="mt-2">
          <Link
            to="/demands"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-200/60 text-foreground-600 text-sm font-medium hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            返回需求大厅
          </Link>
        </div>
      }
      comments={
        demand.id ? (
          <CommentSection
            targetType="DEMAND"
            targetId={demand.id}
            title="需求讨论与留言"
            isTargetAuthor={isSelfPublished}
          />
        ) : null
      }
      modals={
        <>
          {/* Login / Residency Prompt Modal */}
          {showLoginPrompt && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={closeModal}></div>
              <div className="relative bg-white rounded-2xl p-6 md:p-8 w-full max-w-sm shadow-xl">
                <button onClick={closeModal} className="absolute top-4 right-4 w-8 h-8 rounded-lg hover:bg-gray-50 flex items-center justify-center transition-all cursor-pointer">
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-shield-user-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-center font-heading font-bold text-lg text-foreground-950 mb-2">
                  {!isLoggedIn ? '登录后竞标' : '需要完成市场入驻'}
                </h3>
                <p className="text-center text-sm text-foreground-500 mb-6 leading-relaxed">
                  {!isLoggedIn
                    ? '请先登录账号，且入驻市场后即可参与需求竞标。'
                    : '参与需求竞标需要入驻市场后请先申请或完成入驻。'}
                </p>
                <div className="flex flex-col gap-2.5">
                  {!isLoggedIn ? (
                    <Link
                      to="/auth"
                      onClick={closeModal}
                      className="w-full text-center px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:brightness-95 transition-all cursor-pointer whitespace-nowrap"
                    >
                      登录 / 注册
                    </Link>
                  ) : (
                    <Link
                      to="/certification"
                      onClick={closeModal}
                      className="w-full text-center px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:brightness-95 transition-all cursor-pointer whitespace-nowrap"
                    >
                      去申请入驻
                    </Link>
                  )}
                  <button
                    onClick={closeModal}
                    className="w-full px-5 py-2.5 rounded-lg border border-gray-200/60 text-foreground-600 text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    稍后再说
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Bid Modal */}
          {showBidModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" onClick={closeModal}></div>
              <div className="relative bg-white rounded-2xl p-6 md:p-8 w-full max-w-md shadow-xl">
                {bidSubmitted ? (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                      <i className="ri-check-line text-3xl text-primary-600"></i>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2">竞标提交成功</h3>
                    <p className="text-sm text-foreground-500 mb-6">您的竞标信息已发送给需求方，请耐心等待审核。</p>
                    <button
                      onClick={closeModal}
                      className="px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:brightness-95 transition-all cursor-pointer whitespace-nowrap"
                    >
                      知道了
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="font-heading font-bold text-lg text-foreground-950">提交竞标</h3>
                      <button onClick={closeModal} className="w-8 h-8 rounded-lg hover:bg-gray-50 flex items-center justify-center transition-all cursor-pointer">
                        <i className="ri-close-line text-foreground-500"></i>
                      </button>
                    </div>

                    <div className="mb-5 p-3 rounded-lg bg-gray-50 border border-gray-200/60">
                      <p className="text-xs text-foreground-500 mb-1">竞标项目</p>
                      <p className="text-sm font-semibold text-foreground-950 line-clamp-1">{demand.title}</p>
                    </div>

                    <form onSubmit={handleBidSubmit} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          报价金额（元）
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm">¥</span>
                          <input
                            type="number"
                            value={bidPrice}
                            onChange={(e) => setBidPrice(e.target.value)}
                            placeholder="请输入您的报价"
                            className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 focus:ring-2 focus:ring-primary-100/50 transition-all"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          交付周期（天）
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            value={bidDays}
                            onChange={(e) => setBidDays(e.target.value)}
                            placeholder="预计完成所需天数"
                            className="w-full pl-4 pr-10 py-2.5 rounded-xl border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 focus:ring-2 focus:ring-primary-100/50 transition-all"
                            required
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs">天</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                          竞标说明 <span className="text-foreground-400 font-normal">（选填，最多 500 字）</span>
                        </label>
                        <textarea
                          value={bidNote}
                          onChange={(e) => setBidNote(e.target.value)}
                          placeholder="简要说明您的优势、过往案例或交付方案..."
                          rows={4}
                          maxLength={500}
                          className="w-full px-4 py-2.5 rounded-xl border border-background-300/60 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 focus:ring-2 focus:ring-primary-100/50 transition-all resize-none"
                        />
                        <p className="text-right text-xs text-foreground-400 mt-1">{bidNote.length} / 500</p>
                      </div>

                      {bidError && (
                        <p className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">{bidError}</p>
                      )}

                      <div className="flex gap-3 pt-2">
                        <button
                          type="button"
                          onClick={closeModal}
                          className="flex-1 px-5 py-2.5 rounded-lg border border-gray-200/60 text-foreground-600 text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                        >
                          取消
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmittingBid}
                          className="flex-1 px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:brightness-95 transition-all cursor-pointer whitespace-nowrap shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSubmittingBid ? '提交中...' : '确认提交'}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </>
      }
    >
      {/* Quick Info Bar */}
      <div className="flex flex-wrap items-center gap-4 md:gap-6 px-6 py-4 rounded-2xl bg-white border border-gray-200/80 text-xs sm:text-sm text-foreground-500 shadow-2xs">
        <span className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center">
            <i className="ri-building-line text-secondary-600 text-sm"></i>
          </div>
          <span className="font-medium text-foreground-700">
            {demand.enterprise_name_masked || '官方认证需求'}
          </span>
        </span>
        {deliveryText && (
          <>
            <span className="w-px h-5 bg-background-200"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-foreground-400 text-sm"></i>
              交付: {deliveryText}
            </span>
          </>
        )}
        {deadlineText && (
          <>
            <span className="w-px h-5 bg-background-200"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-foreground-400 text-sm"></i>
              截止: {deadlineText}
            </span>
          </>
        )}
        {publishedText && (
          <>
            <span className="w-px h-5 bg-background-200"></span>
            <span className="text-foreground-400">
              发布于 {publishedText}
            </span>
          </>
        )}
      </div>

      {/* Description Card */}
      <div className="rounded-2xl bg-white border border-gray-200/80 p-6 md:p-8 shadow-2xs">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center">
            <i className="ri-file-text-line text-primary-600"></i>
          </div>
          <h2 className="font-heading font-bold text-lg text-foreground-950">需求详细描述</h2>
        </div>
        <p className="text-sm sm:text-base text-foreground-700 leading-relaxed whitespace-pre-line">
          {demand.description || demand.description_summary || '暂无需求描述内容'}
        </p>
      </div>

      {/* Requirements Card */}
      {requirements.length > 0 && (
        <div className="rounded-2xl bg-white border border-gray-200/80 p-6 md:p-8 shadow-2xs">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
              <i className="ri-checkbox-circle-line text-amber-600"></i>
            </div>
            <h2 className="font-heading font-bold text-lg text-foreground-950">承接要求</h2>
          </div>
          <ul className="space-y-3">
            {requirements.map((req, idx) => (
              <li key={idx} className="flex items-start gap-3 p-3.5 md:p-4 rounded-xl bg-gray-50/50 border border-gray-200/60 text-sm text-foreground-700 hover:border-primary-200/80 transition-colors">
                <span className="shrink-0 w-6 h-6 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center text-xs font-bold">
                  {idx + 1}
                </span>
                <span className="pt-0.5">{req}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Attachments Card */}
      {demand.attachments && demand.attachments.length > 0 && (
        <div className="rounded-2xl bg-white border border-gray-200/80 p-6 md:p-8 shadow-2xs">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
              <i className="ri-attachment-line text-blue-600"></i>
            </div>
            <h2 className="font-heading font-bold text-lg text-foreground-950">需求附件</h2>
          </div>
          <div className="space-y-2.5">
            {demand.attachments.map((att, idx) => (
              <div key={idx} className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200/70 hover:border-primary-200 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <i className="ri-file-line text-lg text-foreground-400"></i>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">
                      {att.display_name || att.file_name || '附件文件'}
                    </p>
                    {att.file_size && (
                      <p className="text-xs text-foreground-400">{formatFileSize(att.file_size)}</p>
                    )}
                  </div>
                </div>
                {att.access_url && (
                  <a
                    href={att.access_url}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded-lg bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer"
                  >
                    下载附件
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </DetailPageLayout>
  );
}
